import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
    AppBar,
    Container,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Toolbar,
    useMediaQuery,
    useScrollTrigger,
    useTheme,
} from "@mui/material";
import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 10,
  });

  // Legal/policy pages should always have dark navbar
  const legalPages = ['/privacy', '/terms', '/refund'];
  const isLegalPage = legalPages.includes(location.pathname);
  const shouldBeDark = isLegalPage || trigger;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSubscribeClick = () => {
    if (location.pathname === "/") {
      // Already on homepage, scroll to quiz section
      const quizElement = document.getElementById("quiz-section");
      if (quizElement) {
        quizElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Navigate to homepage with hash
      navigate("/#quiz-section");
      // After navigation, scroll to the element
      setTimeout(() => {
        const quizElement = document.getElementById("quiz-section");
        if (quizElement) {
          quizElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: "About", path: "/about" },
  ];

  const drawer = (
    <MKBox sx={{ width: 250, backgroundColor: "#000000" }} role="presentation">
      <MKBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderBottom="1px solid rgba(255, 255, 255, 0.1)"
      >
          <MKBox display="flex" alignItems="center" gap={1.5}>
            <MKBox
              component="img"
              src="/workoutaiplan.png"
              alt="WorkoutAIPlan Logo"
              sx={{ width: 28, height: 28 }}
            />
            <MKTypography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontFamily: "'Oswald', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                fontSize: "1.5rem",
                color: "#ffffff",
                "& .highlight": { color: "#FF6B35" }
              }}
            >
              WORKOUT<span className="highlight">AI</span>PLAN
            </MKTypography>
          </MKBox>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "#ffffff" }}>
          <CloseIcon />
        </IconButton>
      </MKBox>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                py: 1.5,
                px: 3,
                "& .MuiListItemText-primary": {
                  color: "rgba(255, 255, 255, 0.8)",
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase"
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 107, 53, 0.1)",
                  "& .MuiListItemText-primary": {
                    color: "#FF6B35"
                  }
                }
              }}
            >
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem sx={{ px: 3, pt: 2 }}>
          <MKButton
            variant="gradient"
            fullWidth
            onClick={() => {
              handleDrawerToggle();
              handleSubscribeClick();
            }}
            sx={{
              background: "#FF6B35",
              color: "#ffffff",
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 600,
              fontSize: "0.875rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              py: 1.5,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(255, 107, 53, 0.2)",
              "&:hover": {
                background: "#E85A2A",
                boxShadow: "0 6px 16px rgba(255, 107, 53, 0.3)",
              },
            }}
          >
            Get Started
          </MKButton>
        </ListItem>
      </List>
    </MKBox>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={shouldBeDark ? 2 : 0}
        sx={{
          backgroundColor: shouldBeDark ? "rgba(0, 0, 0, 0.95)" : "transparent",
          backdropFilter: shouldBeDark ? "blur(10px)" : "none",
          transition: "all 0.3s ease",
          borderBottom: shouldBeDark ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
          boxShadow: "none",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: "70px" }}>
            <MKBox
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
                flexGrow: isMobile ? 1 : 0,
                mr: isMobile ? 0 : 4,
              }}
            >
              {/* Logo */}
              <MKBox
                component="img"
                src="/workoutaiplan.png"
                alt="WorkoutAIPlan Logo"
                sx={{
                  width: 32,
                  height: 32,
                  display: "block",
                }}
              />
              <MKTypography
                variant="h5"
                fontWeight="bold"
                sx={{
                  lineHeight: 1,
                  fontFamily: "'Oswald', sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                  fontSize: "1.6rem",
                  color: "#ffffff",
                  "& .highlight": { color: "#FF6B35" }
                }}
              >
                WORKOUT<span className="highlight">AI</span>PLAN
              </MKTypography>
            </MKBox>

            {!isMobile && (
              <>
                <MKBox sx={{ flexGrow: 1, display: "flex", gap: 1 }}>
                  {navItems.map((item) => (
                    <MKButton
                      key={item.name}
                      component={Link}
                      to={item.path}
                      variant="text"
                      sx={{
                        px: 2,
                        color: "rgba(255, 255, 255, 0.8)",
                        fontFamily: "'Oswald', sans-serif",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        "&:hover": {
                          color: "#FF6B35",
                          backgroundColor: "transparent"
                        }
                      }}
                    >
                      {item.name}
                    </MKButton>
                  ))}
                </MKBox>
                <MKButton
                  variant="gradient"
                  onClick={handleSubscribeClick}
                  sx={{
                    background: "#FF6B35",
                    color: "#ffffff",
                    fontFamily: "'Oswald', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    px: 3,
                    py: 1.25,
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.2)",
                    "&:hover": {
                      background: "#E85A2A",
                      boxShadow: "0 6px 16px rgba(255, 107, 53, 0.3)",
                    },
                  }}
                >
                  Get Started
                </MKButton>
              </>
            )}

            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ color: "#ffffff" }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: {
              backgroundColor: "transparent",
            }
          }
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer to prevent content from hiding under fixed navbar */}
      <Toolbar sx={{ minHeight: "70px" }} />
    </>
  );
}

export default Navbar;

